import { EZChatControllerNew } from "@axl/ez-chat-for-school";
import {
  mountSchoolChat,
  type SchoolChatHandle,
} from "@axl/site-chat/src/school/mountSchoolChat";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { appSettings } from "@app/shared/lib/app-settings";
import {
  useSchool,
  useSdk,
  useTokenStorage,
  useUser,
} from "@app/shared/lib/hooks";

/**
 * `pending` — asking AccelChat whether the school has a membership chat; `site` — it has: the website chat
 * (`@axl/site-chat` in its School mode) replaces the old one; `legacy` — it has not (none, disabled or not marked
 * for the membership area): the old support chat, exactly as before.
 */
type ChatKind = "pending" | "site" | "legacy";

type ChatContextType = {
  chatController: EZChatControllerNew | null;
  setChatController: (controller: EZChatControllerNew | null) => void;
  isChatInited: boolean;
  unreadCount: number;
  kind: ChatKind;
};

const ChatContext = createContext<ChatContextType>({} as ChatContextType);

/** AccelChat's root: School's chat API setting ends with `/api`, the website chat adds its own paths. */
const chatRoot = () =>
  String(appSettings.chat.apiUrl ?? "").replace(/\/api\/?$/, "");

/**
 * The website chat's handle, dressed as the old controller: the header button and the "technical support" links
 * call `open()` / `close()` / `visible` and need not know which chat is behind them.
 */
const asController = (chat: SchoolChatHandle) =>
  ({
    open: () => chat.open(),
    close: () => chat.close(),
    get visible() {
      return chat.isOpen;
    },
  }) as unknown as EZChatControllerNew;

export const ChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useSdk();
  const tokenStorage = useTokenStorage();
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = tokenStorage.accessToken;
  // The chat belongs to the signed-in student, not to "some token": switching accounts (login-as, a login link,
  // signing in again) replaces the token without it ever going empty, and a chat keyed on "signed in" kept the
  // previous student's session and thread.
  const { user } = useUser();
  const userId = tokenStorage.accessToken ? user?.id : undefined;

  const [kind, setKind] = useState<ChatKind>("pending");
  const [siteController, setSiteController] =
    useState<EZChatControllerNew | null>(null);
  const [legacyController, setChatController] =
    useState<EZChatControllerNew | null>(null);
  const [isChatInited, setChatInited] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    setKind("pending");
    setChatInited(false);
    setUnreadCount(0);
    if (!userId) return;
    const chat = mountSchoolChat({
      apiUrl: chatRoot(),
      // The SDK's own cache first: it holds the new student's token before their profile arrives, while the
      // polled `tokenStorage` may still be a second behind.
      getToken: () => sdk.tokenStorage.tokens?.accessToken ?? tokenRef.current,
    });
    let alive = true;
    const unsubscribe = chat.subscribe(() => setUnreadCount(chat.unread));
    chat.ready
      .then((ok) => {
        if (!alive) return;
        if (!ok) {
          setKind("legacy");
          return;
        }
        setSiteController(asController(chat));
        setChatInited(true);
        setUnreadCount(chat.unread);
        setKind("site");
      })
      .catch(() => alive && setKind("legacy"));
    return () => {
      alive = false;
      unsubscribe();
      chat.dispose();
      setSiteController(null);
    };
  }, [userId, sdk]);

  useEffect(() => {
    if (kind !== "legacy" || !legacyController) return;
    const handler = () => setChatInited(true);
    (legacyController as any).onInited.on(handler);
    return () => (legacyController as any).onInited.off(handler);
  }, [kind, legacyController]);

  useEffect(() => {
    if (kind !== "legacy" || !legacyController || !isChatInited) return;
    const handler = (count: number) => setUnreadCount(count);
    legacyController.onUnreadCountChanged.on(handler);
    return () => legacyController.onUnreadCountChanged.off(handler);
  }, [kind, legacyController, isChatInited]);

  const ctxValue = {
    chatController: kind === "site" ? siteController : legacyController,
    setChatController,
    isChatInited,
    unreadCount,
    kind,
  };

  return (
    <ChatContext.Provider value={ctxValue}>{children}</ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);

/**
 * Whether the student has a chat at all: the membership chat when the school has one, otherwise the old support
 * chat under its own switch (`supportChatEnabled`).
 */
export const useChatAvailable = () => {
  const school = useSchool();
  const { kind } = useChat();
  return kind === "site" || (kind === "legacy" && school.supportChatEnabled);
};
